import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, fmtDateTime } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { TransferPlanForm } from "@/components/TransferPlanForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function PlanStatusBadge({ status, testid }) {
  return status === "completed" ? (
    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={testid}>Completed</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid={testid}>Planned</Badge>
  );
}

export default function TransferPlans() {
  const [plans, setPlans] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [pl, wh, pr] = await Promise.all([
        API.get("/transfer-plans"),
        API.get("/warehouses"),
        API.get("/products"),
      ]);
      setPlans(pl.data);
      setWarehouses(wh.data);
      setProducts(pr.data.filter((p) => (p.type || "product") !== "service"));
    } catch {
      toast.error("Could not load transfer plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await API.delete(`/transfer-plans/${id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete plan");
    }
  };

  const plannedTotal = (p) => p.lines.reduce((a, l) => a + (l.planned_qty || 0), 0);
  const shortfalls = (p) => p.lines.filter((l) => l.resolution !== "pending" && (l.confirmed_qty || 0) < (l.planned_qty || 0)).length;

  return (
    <div className="space-y-6" data-testid="transfer-plans-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Transfer Plans</h1>
          <p className="text-sm text-slate-500">Plan stock movement between Company 1 warehouses, then confirm at the warehouse</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setFormOpen(true)} data-testid="new-transfer-plan-button">
          <Plus className="mr-1 h-4 w-4" /> New Transfer Plan
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="transfer-plans-loading">Loading plans...</div>
      ) : plans.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <EmptyState title="No transfer plans yet" hint="Create a plan, then confirm quantities on your phone at the warehouse." />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="transfer-plans-list">
          {plans.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 bg-white p-4 transition-shadow duration-150 hover:shadow-md" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-900">
                  <span className="truncate">{p.source_warehouse_name}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span className="truncate">{p.dest_warehouse_name}</span>
                </div>
                <PlanStatusBadge status={p.status} testid={`plan-status-${p.id}`} />
              </div>
              <div className="mt-2 text-xs text-slate-500" data-testid={`plan-meta-${p.id}`}>
                {p.lines.length} item{p.lines.length === 1 ? "" : "s"} · {plannedTotal(p)} pcs planned
                {p.status === "completed" && shortfalls(p) > 0 && (
                  <span className="ml-1 font-medium text-amber-600" data-testid={`plan-shortfall-${p.id}`}>
                    · {shortfalls(p)} shortfall{shortfalls(p) === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {p.created_by_name} · {fmtDateTime(p.created_at)}
              </div>
              {p.note && <div className="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">{p.note}</div>}
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" asChild data-testid={`plan-open-${p.id}`}>
                  <Link to={`/transfer-plans/${p.id}`}>{p.status === "completed" ? "View" : "Open & Execute"}</Link>
                </Button>
                {p.status === "planned" && (
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`} aria-label="Delete plan">
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <TransferPlanForm open={formOpen} onOpenChange={setFormOpen} warehouses={warehouses} products={products} plan={null} onSaved={load} />
    </div>
  );
}
